import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useGuestImageGeneration } from '@/hooks/useGuestImageGeneration';
import { useAvailableModels } from '@/hooks/useAvailableModels';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { Loader2, Download, Eye, Lock, Users, Sparkles, Bug, Trash2, BookOpen, Image as ImageIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { SignupModal } from '@/components/ui/signup-modal';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import GPTModelTimeoutNotification, { showGPTGenerationToast } from './GPTModelTimeoutNotification';
import TrendingPromptSuggestions from './TrendingPromptSuggestions';
import type { TrendingPrompt } from '@/data/trendingPrompts';
const GuestImageGeneration = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [size, setSize] = useState('1024x1024');
  const [quality, setQuality] = useState('standard');
  const [style, setStyle] = useState('natural');
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [signupModalAction, setSignupModalAction] = useState<'view' | 'download'>('view');

  // Check for selected prompt from library
  useEffect(() => {
    const selectedPrompt = localStorage.getItem('selectedPrompt');
    if (selectedPrompt) {
      setPrompt(selectedPrompt);
      localStorage.removeItem('selectedPrompt');
      toast.success('Prompt loaded from library!');
    }
  }, []);
  const {
    models: availableModels = [],
    loading: modelsLoading
  } = useAvailableModels();
  const {
    isLoading: generating,
    remainingGenerations,
    totalLimit,
    generatedImages,
    generateImage,
    hasGuestImages,
    removeImageAt,
    clearImages,
    claimImages
  } = useGuestImageGeneration();
  const {
    value: bugReportUrl,
    loading: bugReportLoading
  } = useSiteConfigValue('bug_report_form_url');
  const { user } = useSecureAuthContext();

  // Get supported options from selected model
  const selectedModelConfig = availableModels.find(m => m.model_key === selectedModel);
  const supportedSizes = selectedModelConfig?.supported_sizes || ['1024x1024', '1792x1024', '1024x1792'];
  const supportedQualities = selectedModelConfig?.supported_qualities || ['standard'];

  // Set default model when available
  useEffect(() => {
    if (availableModels.length > 0 && !selectedModel) {
      setSelectedModel(availableModels[0].model_key);
    }
  }, [availableModels, selectedModel]);

  // Reset size/quality when model changes if they're not supported
  useEffect(() => {
    if (selectedModelConfig) {
      // Reset size if current size is not supported
      if (!supportedSizes.includes(size)) {
        setSize(supportedSizes[0]);
      }
      // Reset quality if current quality is not supported
      if (!supportedQualities.includes(quality)) {
        setQuality(supportedQualities[0]);
      }
    }
  }, [selectedModelConfig, supportedSizes, supportedQualities, size, quality]);

  // Redirect to dashboard when user logs in (claiming will happen there)
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);
  const handleGenerate = async () => {
    if (!prompt.trim() || generating) return;
    if (remainingGenerations <= 0) {
      toast.error('Daily limit reached! Sign up to continue generating images.');
      return;
    }
    
    // Show GPT timeout notification if using GPT model
    showGPTGenerationToast(selectedModel);
    
    await generateImage({
      prompt: prompt.trim(),
      model: selectedModel,
      size,
      quality,
      style,
      numberOfImages: remainingGenerations
    });
  };
  const handleViewAttempt = () => {
    setSignupModalAction('view');
    setSignupModalOpen(true);
  };
  const handleDownloadAttempt = () => {
    setSignupModalAction('download');
    setSignupModalOpen(true);
  };
  const handleRemoveImage = (index: number) => {
    removeImageAt(index);
    toast.success('Removed image from session');
  };
  const handleClearImages = () => {
    if (generatedImages.length === 0) return;
    clearImages();
    toast.success('Cleared all generated images');
  };
  const handleBugReport = () => {
    const url = bugReportUrl && bugReportUrl.trim() !== '' ? bugReportUrl : 'https://forms.gle/vwv1nkkQSvSj8ts49';
    window.open(url, '_blank');
  };

  const handleSelectPrompt = (promptData: TrendingPrompt) => {
    setPrompt(promptData.prompt);
    setSize(promptData.suggestedSize);
    setQuality(promptData.estimatedQuality);
    
    // Scroll to prompt textarea and focus it
    setTimeout(() => {
      const textarea = document.querySelector('textarea');
      if (textarea) {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textarea.focus();
      }
    }, 100);
    
    toast.success('Prompt loaded! Feel free to customize it.');
  };
  if (modelsLoading) {
    return <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading AI models...</p>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-cyan-600/5 via-transparent to-pink-600/5"></div>
      </div>

      {/* Animated background elements */}
      <div className="absolute w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse top-1/4 left-1/4"></div>
      <div className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse bottom-1/4 right-1/4"></div>

      {/* Header */}
      <div className="bg-black/20 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50 shadow-2xl">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <h1 className="text-xl font-semibold text-white">Try AI Image Generator</h1>
              </div>
              <Badge className="bg-white/10 text-white border-white/20 hidden sm:inline-flex">
                {remainingGenerations} generations left
              </Badge>
            </div>
            <div className="flex items-center space-x-2">
              {(() => {
                if (user) {
                  return (
                    <Link to="/dashboard">
                      <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0">
                        Go to Dashboard
                      </Button>
                    </Link>
                  );
                } else {
                  return (
                    <>
                      <Link to="/auth">
                        <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 border border-white/20">
                          Sign In
                        </Button>
                      </Link>
                      <Link to="/auth?mode=signup">
                        <Button size="sm" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0">
                          Sign Up Free
                        </Button>
                      </Link>
                    </>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Guest Info Alert */}
          <Alert className="border-blue-500/20 bg-white/5 backdrop-blur-sm text-white">
            <Users className="h-4 w-4 text-blue-400" />
            <AlertDescription className="text-gray-300">
              <strong className="text-white">Guest Mode:</strong> You can generate {remainingGenerations} images without signing up. 
              <Link to="/auth?mode=signup" className="underline ml-1 font-medium text-blue-400 hover:text-blue-300">
                Create a free account
              </Link> for unlimited generations and downloads!
            </AlertDescription>
          </Alert>

          {/* Community Gallery Notice */}
          <Alert className="border-purple-500/20 bg-purple-500/10 backdrop-blur-sm text-white">
            <ImageIcon className="h-4 w-4 text-purple-400" />
            <AlertDescription className="text-gray-300">
              <strong className="text-white">Community Showcase:</strong> Your generated images will be featured in our public gallery 
              where anyone can view and download them. This helps inspire the community! 
              Avoid including personal or sensitive content.
            </AlertDescription>
          </Alert>

          {/* Trending Prompts */}
          <TrendingPromptSuggestions onSelectPrompt={handleSelectPrompt} />

          {/* Generation Form */}
          <Card className="bg-black/20 backdrop-blur-xl border-white/10 text-white">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Generate AI Image</span>
                <div className="flex items-center gap-2">
                  <Badge variant={remainingGenerations > 0 ? "default" : "destructive"}>
                    {remainingGenerations}/{totalLimit} left today
                  </Badge>
                  <Button size="sm" asChild variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                    <Link to="/prompt-library">
                      <BookOpen className="w-3 h-3 mr-1" />
                      Browse Templates
                    </Link>
                  </Button>
                  <Button size="sm" onClick={handleBugReport} disabled={bugReportLoading} className="bg-gradient-to-r from-bugReport-from to-bugReport-to hover:from-bugReport-hoverFrom hover:to-bugReport-hoverTo text-white shadow-lg hover:shadow-xl transition-all duration-300">
                    <Bug className="w-3 h-3 mr-1" />
                    Report Bug
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Prompt Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-white">Describe your image</label>
                <Textarea placeholder="A serene landscape with mountains and a lake at sunset..." value={prompt} onChange={e => setPrompt(e.target.value)} className="min-h-[100px] bg-white/10 border-white/20 text-white placeholder:text-gray-400" />
                <p className="text-xs text-gray-400">
                  {prompt.length}/1000 characters (guest limit)
                </p>
              </div>

              {/* GPT Model Timeout Notification */}
              <GPTModelTimeoutNotification selectedModel={selectedModel} />

              {/* Model Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">AI Model</label>
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/20 text-white">
                      {availableModels.map(model => <SelectItem key={model.model_key} value={model.model_key}>
                          {model.display_name}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Size</label>
                  <Select value={size} onValueChange={setSize}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/20 text-white">
                      {supportedSizes.map(sizeOption => {
                      const [width, height] = sizeOption.split('x');
                      const isSquare = width === height;
                      const isPortrait = parseInt(height) > parseInt(width);
                      const isLandscape = parseInt(width) > parseInt(height);
                      const label = isSquare ? 'Square' : isPortrait ? 'Portrait' : isLandscape ? 'Landscape' : '';
                      return <SelectItem key={sizeOption} value={sizeOption}>
                            {sizeOption} {label && `(${label})`}
                          </SelectItem>;
                    })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Quality</label>
                  <Select value={quality} onValueChange={setQuality}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/20 text-white">
                      {supportedQualities.map(qualityOption => <SelectItem key={qualityOption} value={qualityOption}>
                          {qualityOption === 'standard' ? 'Standard' : qualityOption === 'hd' ? 'HD' : qualityOption.charAt(0).toUpperCase() + qualityOption.slice(1)}
                          {qualityOption !== 'standard' && ' (Sign up required)'}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Style</label>
                  <Select value={style} onValueChange={setStyle}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/20 text-white">
                      <SelectItem value="natural">Natural</SelectItem>
                      <SelectItem value="vivid">Vivid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Generate Button */}
              <Button onClick={handleGenerate} disabled={!prompt.trim() || generating || remainingGenerations <= 0} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0" size="lg">
                {generating ? <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating {remainingGenerations} image{remainingGenerations > 1 ? 's' : ''}...
                  </> : remainingGenerations <= 0 ? 'Daily Limit Reached - Sign Up for More' : `Generate ${remainingGenerations} Image${remainingGenerations > 1 ? 's' : ''}`}
              </Button>

              {remainingGenerations <= 0 && <div className="text-center">
                  <Link to="/auth">
                    <Button variant="outline" className="w-full text-white border-white/20 bg-[#802694]/0">
                      Create Free Account for Unlimited Generations
                    </Button>
                  </Link>
                </div>}
            </CardContent>
          </Card>

          {/* Generated Images */}
          {generatedImages.length > 0 && <Card className="bg-black/20 backdrop-blur-xl border-white/10 text-white">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-white">
                  <span>Your Generated Images</span>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-white/10 text-white border-white/20">{generatedImages.length} images</Badge>
                    <Badge variant={remainingGenerations > 0 ? "default" : "destructive"}>
                      {remainingGenerations}/{totalLimit} left today
                    </Badge>
                    <Button size="sm" variant="outline" onClick={handleClearImages} className="text-white border-white/20 bg-[#784747]/10">
                      Clear All
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {generatedImages.map((image, index) => {
                    const imgSrc = image.image_url || (image.b64_json ? `data:image/png;base64,${image.b64_json}` : '/placeholder.svg');
                    return <div key={image.id || index} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                        <img 
                          src={imgSrc} 
                          alt={image.prompt || 'Generated image'} 
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            console.error('Image load error for:', imgSrc?.substring(0, 50));
                            e.currentTarget.src = '/placeholder.svg';
                          }}
                        />
                        
                        {/* Overlay with buttons */}
                        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2 z-10 backdrop-blur-sm">
                          <Button size="sm" variant="secondary" onClick={handleViewAttempt} className="bg-white/90 hover:bg-white text-black border border-white/50 shadow-lg font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 h-auto">
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            <span className="hidden sm:inline">View</span>
                          </Button>
                          <Button size="sm" variant="secondary" onClick={handleDownloadAttempt} className="bg-blue-600/90 hover:bg-blue-600 text-white border border-blue-500/50 shadow-lg font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 h-auto">
                            <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            <span className="hidden sm:inline">Download</span>
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => handleRemoveImage(index)} className="bg-red-600/90 hover:bg-red-600 text-white border border-red-500/50 shadow-lg font-medium text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2 h-auto">
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            <span className="hidden sm:inline">Remove</span>
                          </Button>
                        </div>
                      </div>

                      {/* Image Info */}
                      <div className="mt-2 space-y-1">
                        {(image as any)?.expired && <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 w-fit">Preview expired</Badge>}
                        <p className="text-xs text-gray-400 line-clamp-2">
                          {image.prompt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {availableModels.find(m => m.model_key === image.model)?.display_name || image.model}
                          </span>
                          <span>{image.size}</span>
                        </div>
                      </div>
                    </div>;
                  })}
                </div>

                {hasGuestImages && <div className="mt-6 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20 backdrop-blur-sm">
                    <div className="flex items-center space-x-3">
                      <Lock className="h-5 w-5 text-blue-400" />
                      <div className="flex-1">
                        <h4 className="font-medium text-white">Unlock Full Resolution</h4>
                        <p className="text-sm text-gray-400">
                          Sign up to download these images in full resolution and generate unlimited more!
                        </p>
                      </div>
                      <Link to="/auth?mode=signup">
                        <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0">
                          Sign Up Free
                        </Button>
                      </Link>
                    </div>
                  </div>}
              </CardContent>
            </Card>}

          {/* Call to Action */}
          {!hasGuestImages && <Card className="text-center bg-black/20 backdrop-blur-xl border-white/10 text-white">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-white">Ready to Create Amazing AI Art?</h3>
                <p className="text-gray-400 mb-6">
                  Generate your first image above, or sign up for unlimited access to all features!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/auth?mode=signup">
                    <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0">
                      Create Free Account
                    </Button>
                  </Link>
                  <Link to="/auth">
                    <Button variant="outline" size="lg" className="w-full sm:w-auto text-white border-white/20 bg-white/[0.04]">
                      Sign In
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>}

          {/* Signup Modal */}
          <SignupModal open={signupModalOpen} onOpenChange={setSignupModalOpen} action={signupModalAction} />
        </div>
      </div>
    </div>;
};
export default GuestImageGeneration;